import {Inject, injectable} from 'inversify-props';
import {TypedJSON} from 'typedjson';

import {Timezone} from '@/model/system/time/timezone';
import {NtpServer} from '@/model/system/time/ntpServer';

import {IApiService} from '@/services/IApi.service';
import {ISystemTimeService} from '@/services/system/ITime.service';

@injectable()
export class SystemTimeService implements ISystemTimeService {

  @Inject(Symbol.for('IApiService'))
  private apiService!: IApiService;

  private path: string = '/system/time';

  private ntpServerSerializer = new TypedJSON(NtpServer);

  private timezoneSerializer = new TypedJSON(Timezone);

  public async getDatetime(): Promise<Date> {
    return new Date(await this.apiService.get(this.path));
  }

  public async getAvailableTimezones(): Promise<Timezone[]> {
    const ret = await this.apiService.get(`${this.path}/available-timezones`);
    return ret.map((tz: any) => this.timezoneSerializer.parse(tz));
  }

  public async getTimezone(): Promise<Timezone> {
    return this.timezoneSerializer.parse(await this.apiService.get(`${this.path}/timezone`))!;
  }

  public async setTimezone(timezone: Timezone): Promise<void> {
    return await this.apiService.put(`${this.path}/timezone`, this.timezoneSerializer.toPlainJson(timezone));
  }

  public async getNtpPool(): Promise<string> {
    return await this.apiService.get(`${this.path}/ntp/pool`);
  }

  public async getNtpServers(): Promise<NtpServer[]> {
    const ret = await this.apiService.get(`${this.path}/ntp/servers`);
    return ret?.map((s: any) => this.ntpServerSerializer.parse(s));
  }

  public async setNtpServers(servers: NtpServer[]): Promise<NtpServer[]> {
    const ret = await this.apiService.put(`${this.path}/ntp/servers`, servers.map((s) => this.ntpServerSerializer.toPlainJson(s)));
    return ret?.map((s: any) => this.ntpServerSerializer.parse(s));
  }

  public async restartNtp(): Promise<string> {
    return await this.apiService.post(`${this.path}/ntp/restart`, null);
  }
}
